/**
 * NgwEditorDirection — `IEditorDirection` 의 NgwEditor 본 구현 (PR-N2).
 *
 * 사용자 결정 (a) — DOM `dir` attribute / `spellcheck` property 직접 토글.
 *
 * D-N4 결정: NgwEditor 가 `directionLTR` / `directionRTL` 명령을 노출하지 않음. 모든 ProseMirror
 * 노드가 dir 을 상속하므로 mount element 의 attribute 만 토글하면 본문 전체에 적용된다.
 * 어댑터 자체 ProseMirror plugin 작성은 PR-N3 의 NGW_COMMAND_MAP 정리와 함께.
 *
 * 어댑터 상태 보존:
 *   - `_rtl`: 외부에서 setRTL 으로 마지막 설정된 값. init 전 호출도 안전 (DOM 부재 시 silent).
 *   - `_spellCheck`: 동일.
 *
 * **DOM 우선**: isRTL / getSpellCheck 는 DOM 의 실제 값을 우선 반환. 어댑터 상태는 DOM 부재
 * 시 fallback.
 */

import type { IEditorDirection } from '../IEditorDirection'
import type { NgwEditorAdapter } from './NgwEditorAdapter'

export class NgwEditorDirection implements IEditorDirection {
  private readonly adapter: NgwEditorAdapter
  private rtl = false
  private spellCheck = true

  constructor(adapter: NgwEditorAdapter) {
    this.adapter = adapter
  }

  isRTL(): boolean {
    const dom = this.getEditorDOM()
    if (dom == null) {
      return this.rtl
    }
    return dom.getAttribute('dir') === 'rtl'
  }

  setRTL(rtl: boolean): void {
    this.rtl = rtl
    const dom = this.getEditorDOM()
    if (dom != null) {
      dom.setAttribute('dir', rtl ? 'rtl' : 'ltr')
    }
  }

  setSpellCheck(enabled: boolean): void {
    this.spellCheck = enabled
    const dom = this.getEditorDOM()
    if (dom != null) {
      dom.spellcheck = enabled
    }
    // TODO(PR-N9d 후보): TinyMCE 의 setSpellCheckState (nine_tinymce_desktop.js L6985-L6999) 는
    // dom.spellcheck = v 직후 body.contentEditable 을 brief false→true 토글하여 브라우저가
    // spellcheck 표시를 즉시 갱신하도록 강제한다. 본 구현은 dom.spellcheck 만 변경하므로 일부
    // 브라우저에서 다음 입력 전까지 표시가 갱신되지 않을 수 있다. 사용자 결정 (2026-05-19):
    // 우선순위 낮음 — 사용자 체감 회귀 보고 시 별도 PR 로 보강.
  }

  getSpellCheck(): boolean {
    const dom = this.getEditorDOM()
    if (dom == null) {
      return this.spellCheck
    }
    return dom.spellcheck
  }

  private getEditorDOM(): HTMLElement | null {
    return this.adapter.lifecycle.getHandle()?.getEditorDOM() ?? null
  }
}
